import { useEffect, useState, useCallback } from "react";
import api, { formatApiError, downloadFile } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  FileBarChart,
  FileSpreadsheet,
  Download,
  Printer,
  Loader2,
  CalendarDays,
  Bus,
  Truck,
  Baby,
  Users,
  Crown,
} from "lucide-react";
import { ESTADOS_RESERVA, TIPOS_MODALIDAD, CANALES } from "@/lib/constants";
import { formatFechaLarga } from "@/lib/agenda";
import { nombreTourDisplay } from "@/lib/tourDisplay";

const hoyISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

function ExportReservasCard({ tours }) {
  const [estado, setEstado] = useState("todos");
  const [tourFilter, setTourFilter] = useState("todos");
  const [modalidad, setModalidad] = useState("todos");
  const [canal, setCanal] = useState("todos");
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [busy, setBusy] = useState(false);

  const exportar = async () => {
    setBusy(true);
    try {
      const params = {};
      if (estado !== "todos") params.estado = estado;
      if (tourFilter !== "todos") params.tour_id = tourFilter;
      if (modalidad !== "todos") params.modalidad = modalidad;
      if (canal !== "todos") params.canal = canal;
      if (desde) params.desde = desde;
      if (hasta) params.hasta = hasta;
      const tag = desde || hasta || hoyISO();
      await downloadFile("/reportes/reservas/csv", `reservas_${tag}.csv`, params);
      toast.success("CSV de reservas descargado.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="p-6 space-y-4" data-testid="export-reservas-card">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-md bg-slate-900 text-white shrink-0">
          <Download className="h-5 w-5" />
        </div>
        <div>
          <h3 className="font-heading text-lg font-semibold text-slate-900">Exportar reservas (CSV)</h3>
          <p className="text-sm text-slate-500">
            Descargá las reservas con todos sus campos, aplicando rango de fechas y filtros. Se abre en Excel.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs">Desde</Label>
          <Input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} data-testid="rep-desde" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Hasta</Label>
          <Input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} data-testid="rep-hasta" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Tour</Label>
          <Select value={tourFilter} onValueChange={setTourFilter}>
            <SelectTrigger data-testid="rep-tour"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos los tours</SelectItem>
              {tours.map((t) => <SelectItem key={t.id} value={t.id}>{t.nombre}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Estado</Label>
          <Select value={estado} onValueChange={setEstado}>
            <SelectTrigger data-testid="rep-estado"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos</SelectItem>
              {Object.entries(ESTADOS_RESERVA).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Modalidad</Label>
          <Select value={modalidad} onValueChange={setModalidad}>
            <SelectTrigger data-testid="rep-modalidad"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Toda modalidad</SelectItem>
              {Object.entries(TIPOS_MODALIDAD).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Canal</Label>
          <Select value={canal} onValueChange={setCanal}>
            <SelectTrigger data-testid="rep-canal"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos los canales</SelectItem>
              <SelectItem value="getyourguide">GetYourGuide</SelectItem>
              <SelectItem value="manual">Manual</SelectItem>
              <SelectItem value="bokun">Bokun</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="flex justify-end">
        <Button onClick={exportar} disabled={busy} data-testid="export-reservas-csv" className="gap-2">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          Descargar CSV
        </Button>
      </div>
    </Card>
  );
}

function SalidaReporteBlock({ b }) {
  const VehiculoIcon = b.vehiculo === "Van" ? Truck : Bus;
  const t = b.totales;
  return (
    <div className="rounded-lg border border-slate-200 overflow-hidden break-inside-avoid" data-testid={`reporte-salida-${b.salida_id}`}>
      <div className="bg-slate-50 px-4 py-3 border-b border-slate-200">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-bold text-slate-900 tabular-nums text-base">{b.hora}</span>
          <span className="font-heading font-semibold text-slate-900">{nombreTourDisplay(b.tour_nombre, b.modalidad)}</span>
          {b.es_privado && (
            <span className="inline-flex items-center gap-1 rounded-full bg-violet-50 text-violet-700 border border-violet-200 px-2 py-0.5 text-[10px] font-semibold">
              <Crown className="h-3 w-3" /> Privado
            </span>
          )}
          <span className="inline-flex items-center gap-1 text-[11px] text-slate-500 border border-slate-200 rounded px-1.5 py-0.5">
            {b.modalidad} · <VehiculoIcon className="h-3 w-3" /> {b.vehiculo}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5 text-[12px] text-slate-600">
          <span className="inline-flex items-center gap-1 font-medium text-slate-800">
            <Users className="h-3.5 w-3.5" /> {t.total} pax
          </span>
          <span>{t.adultos} adultos · {t.ninos} niños</span>
          {t.infantes > 0 && (
            <span className="inline-flex items-center gap-1 text-amber-700">
              <Baby className="h-3.5 w-3.5" /> {t.infantes} infante(s) · {b.infante_cuenta ? "ocupan asiento" : "no ocupan asiento"}
            </span>
          )}
          {t.con_saldo > 0 && <span className="text-rose-600 font-medium">{t.con_saldo} con saldo</span>}
          <span className="text-slate-400">Cupo {b.cupo_max}</span>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-white border-b border-slate-100 text-[11px] uppercase tracking-wider text-slate-400">
              <th className="text-left px-4 py-2 font-medium">Pasajero</th>
              <th className="text-left px-3 py-2 font-medium">Modalidad</th>
              <th className="text-left px-3 py-2 font-medium">Pax</th>
              <th className="text-left px-3 py-2 font-medium">Contacto</th>
              <th className="text-left px-3 py-2 font-medium">Pago</th>
            </tr>
          </thead>
          <tbody>
            {b.pasajeros.map((p, i) => (
              <tr key={i} className="border-b border-slate-50 last:border-0">
                <td className="px-4 py-2 font-medium text-slate-900">
                  {p.nombre}
                  <span className="ml-1 text-[10px] text-slate-400 font-mono">{p.codigo_booking}</span>
                </td>
                <td className="px-3 py-2 text-slate-600">{p.modalidad}</td>
                <td className="px-3 py-2 text-slate-600 whitespace-nowrap">
                  {p.adultos}a {p.ninos}n{p.infantes > 0 ? ` ${p.infantes}i` : ""}
                </td>
                <td className="px-3 py-2 text-slate-600 text-[12px]">
                  <div>{p.telefono || "—"}</div>
                  <div className="text-slate-400">{p.email || ""}</div>
                </td>
                <td className="px-3 py-2">
                  {p.pago_estado === "Pagado" ? (
                    <span className="text-emerald-600 text-[12px] font-medium">Pagado</span>
                  ) : (
                    <span className="text-rose-600 text-[12px] font-medium">Saldo ${Number(p.saldo_pendiente || 0).toFixed(0)}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ReporteDiarioCard() {
  const [fecha, setFecha] = useState(hoyISO());
  const [rep, setRep] = useState(null);
  const [loading, setLoading] = useState(false);

  const generar = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reportes/dia", { params: { fecha } });
      setRep(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [fecha]);

  useEffect(() => {
    generar();
  }, [generar]);

  const descargarXlsx = async () => {
    try {
      await downloadFile("/reportes/dia/xlsx", `reporte_${fecha}.xlsx`, { fecha });
      toast.success("Reporte Excel descargado.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const r = rep?.resumen || {};

  return (
    <Card className="p-6 space-y-4" data-testid="reporte-diario-card">
      <div className="flex flex-col sm:flex-row sm:items-end gap-3 justify-between" data-print-hide>
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-slate-900 text-white shrink-0">
            <CalendarDays className="h-5 w-5" />
          </div>
          <div>
            <h3 className="font-heading text-lg font-semibold text-slate-900">Reporte diario por tour</h3>
            <p className="text-sm text-slate-500">Listo para imprimir o enviar al chofer / guía.</p>
          </div>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div className="space-y-1.5">
            <Label className="text-xs">Fecha</Label>
            <Input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} className="w-auto" data-testid="reporte-fecha" />
          </div>
          <Button variant="outline" onClick={generar} disabled={loading} data-testid="reporte-generar">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Generar"}
          </Button>
          <Button variant="outline" onClick={() => window.print()} data-testid="reporte-imprimir" className="gap-2">
            <Printer className="h-4 w-4" /> Imprimir
          </Button>
          <Button onClick={descargarXlsx} data-testid="reporte-xlsx" className="gap-2">
            <FileSpreadsheet className="h-4 w-4" /> Excel (.xlsx)
          </Button>
        </div>
      </div>

      <div className="print-area space-y-4">
        <div className="hidden print:block mb-2">
          <h2 className="font-heading text-xl font-bold text-slate-900">Tours NY · Reporte diario</h2>
        </div>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-1 rounded-md bg-slate-50 border border-slate-200 px-4 py-3 text-sm">
          <span className="font-semibold text-slate-900 capitalize">{formatFechaLarga(fecha)}</span>
          <span className="text-slate-600">{r.salidas || 0} salidas</span>
          <span className="text-slate-600">{r.pasajeros || 0} pasajeros</span>
          <span className="text-slate-600">{r.adultos || 0} adultos · {r.ninos || 0} niños · {r.infantes || 0} infantes</span>
          {r.con_saldo > 0 && <span className="text-rose-600 font-medium">{r.con_saldo} con saldo</span>}
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-32"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
        ) : !rep || rep.salidas.length === 0 ? (
          <div className="text-center text-slate-400 text-sm py-10" data-testid="reporte-empty">
            No hay salidas con pasajeros activos para esta fecha.
          </div>
        ) : (
          <div className="space-y-4">
            {rep.salidas.map((b) => <SalidaReporteBlock key={b.salida_id} b={b} />)}
          </div>
        )}
      </div>
    </Card>
  );
}

export default function ReportesPage() {
  const [tours, setTours] = useState([]);

  useEffect(() => {
    api.get("/tours", { params: { activo: true } }).then(({ data }) => setTours(data)).catch(() => {});
  }, []);

  return (
    <div className="space-y-5" data-testid="reportes-page">
      <div className="flex items-center gap-2" data-print-hide>
        <FileBarChart className="h-5 w-5 text-slate-700" />
        <p className="text-sm text-slate-500">
          Exportaciones de reservas y reporte diario por tour para el equipo de operaciones.
        </p>
      </div>
      <div data-print-hide>
        <ExportReservasCard tours={tours} />
      </div>
      <ReporteDiarioCard />
    </div>
  );
}
